var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useReducer, useRef } from "react";
import { Virtuoso } from "react-virtuoso";
import { compareAsc, isValid } from "date-fns";
import ChatMessage from "../Message";
import S from "./MessageList.styled";
import { messageListInitialState, messageListReducer } from "./reducer";
import DateSeparator from "./DateSeparator";
import { useChannel, useTranslation } from "../../hooks";
import useKeyPress from "../../hooks/useKeyPress";
import useCursor from "../../hooks/useCursor";
const MessageList = () => {
    const [state, dispatch] = useReducer(messageListReducer, messageListInitialState);
    const list = useRef(null);
    const channel = useChannel();
    const strings = useTranslation();
    const refMessages = useRef(null);
    const downPress = useKeyPress("ArrowDown", refMessages);
    const upPress = useKeyPress("ArrowUp", refMessages);
    const cursor = useCursor(state.messages.length, downPress, upPress);
    useEffect(() => {
        var _a, _b;
        const messageActions = (_a = refMessages.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(".outer-message");
        if (messageActions)
            (_b = messageActions[cursor]) === null || _b === void 0 ? void 0 : _b.focus();
    }, [cursor]);
    const loadMore = useCallback((lastId) => __awaiter(void 0, void 0, void 0, function* () {
        dispatch({ type: "loading" });
        try {
            const newMessages = (yield channel.queryMessages(channel.chunkSize, lastId)) || [];
            dispatch({
                type: "loaded",
                messages: newMessages,
                moreDataIsAvailable: newMessages.length === channel.chunkSize,
            });
        }
        catch (_a) {
            dispatch({ type: "loadingFailed" });
        }
    }), [channel]);
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            var _a;
            return (_a = list.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex(state.messages ? state.messages.length - 1 : state.topMostIndex);
        }, 200);
        return () => clearTimeout(timeoutId);
    }, [list, state.topMostIndex, state.messages]);
    useEffect(() => {
        const messages = channel.messages || [];
        dispatch({
            type: "initialize",
            messages,
            moreDataIsAvailable: messages.length === channel.chunkSize,
        });
        const unsubscribeFunctions = [
            channel.on("newMessage", (event) => {
                const { message } = event;
                dispatch({ type: "newMessage", message });
            }),
            channel.on("sendingMessage", (event) => {
                const { message } = event;
                dispatch({ type: "sendingMessage", message });
            }),
            channel.on("messageSent", (event) => {
                const { message } = event;
                dispatch({ type: "messageSent", message });
            }),
            channel.on("messageFailed", (event) => {
                const { message } = event;
                dispatch({ type: "messageFailed", message });
            }),
            channel.on("messageUpdated", (event) => {
                const { message } = event;
                dispatch({ type: "messageUpdated", message });
            }),
            channel.on("messageRemoved", (event) => {
                const { message } = event;
                dispatch({ type: "messageRemoved", serverId: message.serverId });
            }),
            channel.on("connected", () => __awaiter(void 0, void 0, void 0, function* () {
                dispatch({ type: "catchup", fromId: undefined });
            })),
        ];
        return () => {
            unsubscribeFunctions.forEach((f) => f.unsubscribe());
        };
    }, [channel]);
    useEffect(() => {
        if (!state.catchupFromId) {
            return;
        }
        channel === null || channel === void 0 ? void 0 : channel.queryMessages(channel.chunkSize, undefined, state.catchupFromId).then((newMessages) => {
            if (newMessages.length === 0) {
                dispatch({ type: "catchupComplete" });
                return;
            }
            dispatch({ type: "newMessages", messages: newMessages });
            const moreMessagesAvailable = newMessages.length === channel.chunkSize;
            const lastMessage = newMessages.reduce((previous, m) => previous.serverId < m.serverId ? m : previous);
            if (moreMessagesAvailable && lastMessage) {
                dispatch({ type: "catchup", fromId: lastMessage.serverId });
            }
            else {
                dispatch({ type: "catchupComplete" });
            }
        });
    }, [channel, state.catchupFromId]);
    useEffect(() => {
        const checkAndDispatch = (moderators) => {
            if (channel && moderators && moderators.includes(channel.currentUserId)) {
                dispatch({ type: "currentUserIsModerator" });
            }
        };
        const fetchModerators = () => __awaiter(void 0, void 0, void 0, function* () {
            const moderators = yield (channel === null || channel === void 0 ? void 0 : channel.queryModerators(100));
            checkAndDispatch(moderators);
        });
        fetchModerators();
    }, [channel]);
    const isFirst = useCallback((current, previous) => {
        return (current === null || current === void 0 ? void 0 : current.senderId) !== (previous === null || previous === void 0 ? void 0 : previous.senderId);
    }, []);
    const isFirstDate = useCallback((current, previous) => {
        const currentDate = new Date(current === null || current === void 0 ? void 0 : current.date).setHours(0, 0, 0, 0);
        const previousDate = new Date(previous === null || previous === void 0 ? void 0 : previous.date).setHours(0, 0, 0, 0);
        if (isValid(currentDate) && Number.isNaN(previousDate)) {
            return true;
        }
        if (isValid(currentDate) && isValid(previousDate)) {
            return compareAsc(currentDate, previousDate) !== 0;
        }
        return false;
    }, []);
    const Header = () => (React.createElement(S.Loader, { isComplete: !state.moreDataIsAvailable },
        React.createElement(S.Spinner, null)));
    const chatMessage = (virtuosoIndex) => {
        var _a, _b, _c, _d;
        const index = virtuosoIndex - state.firstItemIndex;
        const message = (_a = state.messages) === null || _a === void 0 ? void 0 : _a[index];
        const startsGroup = isFirst(message, (_b = state.messages) === null || _b === void 0 ? void 0 : _b[index - 1]);
        const topDate = isFirstDate(message, (_c = state.messages) === null || _c === void 0 ? void 0 : _c[index - 1]);
        const isLastRead = (message === null || message === void 0 ? void 0 : message.serverId) === ((_d = channel === null || channel === void 0 ? void 0 : channel.lastMessageRead) === null || _d === void 0 ? void 0 : _d.serverId);
        const maxIndex = state.messages.length - 1;
        const showNewBanner = isLastRead && index < maxIndex;
        if (!message)
            return null;
        return (React.createElement(React.Fragment, null,
            topDate && React.createElement(DateSeparator, { date: new Date(message.date) }),
            React.createElement(ChatMessage, { key: message === null || message === void 0 ? void 0 : message.clientId, startsGroup: startsGroup, message: message, status: message === null || message === void 0 ? void 0 : message.status, isChannelModerator: state.isChannelModerator }),
            showNewBanner ? React.createElement(S.NewLine, null, strings.newMessages) : null));
    };
    const handleStartReached = () => {
        var _a, _b;
        if (state.isLoading || !state.moreDataIsAvailable) {
            return;
        }
        loadMore((_b = (_a = state.messages) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.serverId);
    };
    return (React.createElement(S.Wrapper, { "data-testid": "messageList", ref: refMessages }, state.messages.length > 0 && (React.createElement(Virtuoso, { key: `chanell_${channel.id}_${state.messages.length}`, id: `chanell_${channel.id}`, ref: list, firstItemIndex: state.firstItemIndex, totalCount: state.messages.length, components: { Header }, itemContent: chatMessage, initialItemCount: state.messages.length, startReached: handleStartReached, followOutput: "smooth" }))));
};
export default MessageList;
//# sourceMappingURL=MessageList.js.map